import { useEffect, useState, type FormEvent } from 'react';
import { apiErrorText, useRespondToRequestMutation } from '@shared/api';
import { Button } from '@shared/ui/Button';
import { Input, TextArea } from '@shared/ui/Input';
import { Buttons, ErrorText, FieldLabel, Form, Hint } from './parts';

/** Предел файла — как на сервере (`RESPONSE_FILE_LIMIT`). */
const MAX_FILE_BYTES = 50 * 1024 * 1024;

interface Props {
  requestId: string;
  /** Ответ отправлен и заявка выполнена. */
  onDone?: () => void;
  onCancel?: () => void;
  /** Фокус в поле ответа сразу (форма в модальном окне). */
  autoFocus?: boolean;
  /** Идёт отправка — окно с формой не закрывать, иначе ответ потеряется. */
  onBusyChange?: (busy: boolean) => void;
}

/**
 * Ответ снабжения по заявке: текст (что и когда будет) и, если есть, файл —
 * счёт, КП, письмо поставщика. Отправка сразу выполняет заявку по автомату
 * (`в работе → выполнена`), инициатор получает уведомление.
 */
export function RespondForm({ requestId, onDone, onCancel, autoFocus, onBusyChange }: Props) {
  const [respond, { isLoading }] = useRespondToRequestMutation();
  const [text, setText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  // Сброс поля файла после отправки: input type=file неуправляемый.
  const [fileKey, setFileKey] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    onBusyChange?.(isLoading);
  }, [isLoading, onBusyChange]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!text.trim()) return;
    if (file && file.size > MAX_FILE_BYTES) {
      setError('Файл больше 50 МБ — приложите файл поменьше или ссылку в тексте.');
      return;
    }
    setError(null);
    try {
      await respond({ id: requestId, text: text.trim(), file, complete: true }).unwrap();
      setText('');
      setFile(null);
      setFileKey((k) => k + 1);
      onDone?.();
    } catch (err) {
      setError(apiErrorText(err, 'Ответ не отправлен.'));
    }
  }

  return (
    <Form onSubmit={(e) => void submit(e)}>
      <FieldLabel>
        Ответ снабжения
        <TextArea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Что заказано, когда и куда привезут, чего нет и чем заменить"
          maxLength={8000}
          autoFocus={autoFocus}
          required
        />
      </FieldLabel>
      <FieldLabel>
        Файл, если есть (счёт, КП, письмо поставщика) — необязательно
        <Input key={fileKey} type="file" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
      </FieldLabel>
      <Hint>После отправки заявка станет выполненной — автор увидит ответ в карточке заявки.</Hint>
      {error && <ErrorText role="alert">{error}</ErrorText>}
      <Buttons>
        <Button type="submit" $variant="primary" disabled={isLoading || !text.trim()}>
          {isLoading ? 'Отправка…' : 'Отправить ответ'}
        </Button>
        {onCancel && (
          <Button type="button" $variant="secondary" onClick={onCancel} disabled={isLoading}>
            Отмена
          </Button>
        )}
      </Buttons>
    </Form>
  );
}
